import React from 'react';
import { WifiIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

// Shared full-page crash fallback (the "rush-hour" page). Both crash
// surfaces render this — RouteError (router errorElement) and the root
// AppErrorBoundary — so the copy and look can't drift apart again.
//
// The technical detail is deliberately NOT rendered here and there is no
// prop to bring it back: consumers console.error the raw error, the user
// only ever sees the calm copy. API 5xx/429 never route here either —
// those stay per-screen so a background poll can't blank the whole panel.
const COPY = {
  error: {
    title: "We're busier than usual",
    body: 'Something went wrong on our side — most likely a spike in traffic. Give it a moment and try again; your data is safe.',
    button: 'Try again',
  },
  offline: {
    title: 'No internet connection',
    body: "You appear to be offline. Check your connection — we'll reload the page automatically as soon as you're back online.",
    button: 'Reload page',
  },
};

const ErrorPage = ({ variant = 'error', onRetry }) => {
  const copy = COPY[variant] || COPY.error;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-6 text-center dark:bg-slate-900">
      <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
        {variant === 'error' && (
          <span
            aria-hidden="true"
            className="absolute -inset-1.5 rounded-full border-2 border-blue-600 opacity-40 animate-ping motion-reduce:hidden"
          />
        )}
        {variant === 'offline' ? (
          <WifiIcon className="h-8 w-8" aria-hidden="true" />
        ) : (
          // Refresh-with-exclamation: "temporary, try again" rather than the
          // alarm of a warning triangle.
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-8 w-8"
            aria-hidden="true"
          >
            <path d="M12 8v4" />
            <path d="M12 16h.01" />
            <path d="M12 3a9 9 0 1 0 9 9" />
            <path d="M21 5v4h-4" />
            <path d="M21 9a9 9 0 0 0-2.6-4.4" />
          </svg>
        )}
      </div>
      <h1 className="mt-6 text-xl font-semibold text-slate-900 dark:text-slate-100">
        {copy.title}
      </h1>
      <p className="mt-2 max-w-md text-sm text-slate-500 dark:text-slate-400">
        {copy.body}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-6 inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
      >
        <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
        {copy.button}
      </button>
    </div>
  );
};

export default ErrorPage;
